import { chain } from 'lodash';

import { CHUNK_TYPE } from '~/constants/conversationModels';
import veevaLogo from '~/assets/svg/veeva-logo.svg';

import { decodeFromBase64 } from '~/utils/message';
import { updateReferencesListForPreviewMode } from '~/utils/footnotes';
import { isVeevaDocument } from '~/utils/document-utils';

/**
 * Normalizes a citation's text for identity comparisons (dedup/grouping keys only — never used
 * for display, the raw text is always what's rendered). The same source extracted from two
 * different documents can differ only in trailing list-separator punctuation (e.g. one document's
 * extraction ends every entry with ";", another's doesn't) even though it's the same reference.
 * @param {string} text
 * @returns {string}
 */
export const normalizeReferenceIdentity = (text = '') => text.trim().replace(/[;,.\s]+$/, '');

/**
 * A single citation placeholder occurrence, parsed from `[docId(-N), p.M]` text or a
 * `.citation-placeholder` DOM element.
 * @typedef {object} CitationEntry
 * @property {string} id - Document externalId (the snippet-index suffix already stripped off).
 * @property {string} snippetIndex - Snippet index parsed from the placeholder's trailing `-N`, or '0' when absent.
 * @property {string} page - Page number, as captured from the placeholder text.
 */

/**
 * The canonical per-occurrence shape produced by normalizeSecondOrderReference, used throughout
 * the citations pipeline (labeling, deduping, grouping) and by the UI (tooltip title, "Reference
 * found on", passage rendering).
 * @typedef {object} NormalizedSecondOrderReference
 * @property {string} referenceId - Physical, coordinate-based id of this occurrence (e.g.
 *   `docId:pN:cx..:cy..`); empty for synthetic primary-source/unsubstantiated entries. Unique per
 *   occurrence, not per source — never used as identity, see `text`.
 * @property {string} externalId - The second-order source's own externalId, when it has one.
 * @property {string} parentExternalId - The citing (primary) document's externalId.
 * @property {string} citationId - `${id}-${page}-${snippetIndex}` identity of the citing occurrence.
 * @property {number|null} refNum - This page's local footnote number; not reliable as identity across
 *   pages, only usable combined with referenceId to pin down one exact occurrence.
 * @property {string} text - The citation's own text — the primary identity used for grouping/dedup.
 * @property {string} name - Display name, defaults to 'Second-order reference'.
 * @property {number|null} page - Page number of the citing snippet.
 * @property {string} citationKey - Same value as citationId.
 * @property {number|null} snippetIndex - Snippet index of the citing snippet.
 * @property {boolean} unsubstantiated - True when no second-order source was found for this
 *   snippet and the document isn't flagged as a primary source.
 * @property {boolean} isPrimarySource - True when this citation resolves to the document itself
 *   (citation_metadata.is_primary_source), rather than to a distinct second-order source.
 * @property {string} doc_type - Document type, from citation_metadata.doc_type.
 * @property {string} snippet - Base64-encoded citing passage text.
 * @property {string} decodedSnippet - Decoded citing passage text.
 */

/**
 * A NormalizedSecondOrderReference promoted to a labeled "card" by buildSecondOrderReferences —
 * one per unique citation label, carrying every occurrence of that source under `passages`.
 * @typedef {NormalizedSecondOrderReference & {index: number, passages: NormalizedSecondOrderReference[]}} SecondOrderReferenceLabel
 */

const computePositionBasedOnNeighbors = (top, left, bulletPositions) => {
  // Calculate initial position
  let bulletTop = top - 4;
  let bulletLeft = left;

  // Check for overlaps and adjust position
  const bulletSize = 24; // Fixed bullet size
  const minDistance = bulletSize; // Minimum distance between bullets

  let attempts = 0;
  const maxAttempts = 8; // Try 8 different positions around the original

  while (attempts < maxAttempts) {
    let hasOverlap = false;

    // Check against existing bullet positions
    for (const existingPos of bulletPositions) {
      const distance = Math.sqrt(Math.pow(bulletTop - existingPos.top, 2) + Math.pow(bulletLeft - existingPos.left, 2));

      if (distance < minDistance) {
        hasOverlap = true;
        break;
      }
    }

    if (!hasOverlap) {
      break; // No overlap found, use this position
    }

    // Try different offset positions around the original
    const offset = Math.floor(attempts / 2) + 1;
    const direction = attempts % 2 === 0 ? 1 : -1;

    if (attempts < 4) {
      // Try horizontal offsets first
      bulletLeft = left + offset * direction * 30;
    } else {
      // Then try vertical offsets
      bulletTop = top - 4 + offset * direction * 30;
      bulletLeft = left; // Reset to original left position
    }

    attempts++;
  }
  return { bulletTop, bulletLeft };
};

// Transform doc-reference spans to superscript citations in preview mode
export const transformDocReferences = (htmlContent, footnotes, showSecondOrderReferences = false) => {
  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = htmlContent;

  removeCitationBulletsOverlay({ container: tempContainer });

  const CITATION_SEL = 'span[data-doc-id]:not(.transformed)';
  const isCitationEl = (el) => el?.nodeType === 1 && el.matches?.(CITATION_SEL);
  // matches spaces and . , (including non-breaking spaces). These are allowed to sit between citations and still count as “consecutive”.
  const isSkippableText = (n) =>
    n?.nodeType === Node.TEXT_NODE && /^[\s.,\u00A0\u202F\u2009]+$/.test(n.nodeValue || '');

  // 2) Compress consecutive numbers into ranges
  const compress = (nums) =>
    chain(nums)
      .uniq()
      .sortBy()
      .transform((acc, n) => {
        const last = acc[acc.length - 1];
        if (!last || n !== last[1] + 1) acc.push([n, n]);
        else last[1] = n;
      }, [])
      .map(([s, e]) => (s === e ? `${s}` : e === s + 1 ? `${s},${e}` : `${s}–${e}`))
      .value();

  const nextGroup = (start) => {
    const group = [start];
    const between = [];
    let buf = [];
    let p = start.nextSibling;

    while (p) {
      if (isCitationEl(p)) {
        group.push(p);
        between.push(...buf);
        buf = [];
        p = p.nextSibling;
        continue;
      }
      if (isSkippableText(p)) {
        buf.push(p);
        p = p.nextSibling;
        continue;
      }
      break; // hit real content → stop; keep trailing buf
    }
    return { group, between };
  };

  const dict = {};
  let next = 1;
  // Synthetic customFootnote entries for genuine second-order sources, appended to the real
  // footnotes array so updateReferencesListForPreviewMode can match rendered second-order lines
  // (which carry the source's own text, not a document title) back to their assigned number.
  const matchFootnotes = [...(footnotes || [])];

  // Resolves the number(s) a single citation span contributes to its <sup>. A genuine
  // second-order reference replaces the main document's own number — same rule as the live
  // bullet overlay and the printed reference list — so one span can now contribute several
  // numbers, each assigned the next-available one in true document order (first pass, below,
  // over the un-mutated tree) and reused if that same source repeats elsewhere.
  const resolveSpanNums = (span) => {
    const id = span.getAttribute('data-doc-id');
    if (!id) return [];

    const reference = footnotes?.find((ref) => (ref.externalId || ref.id) === id);
    let genuineSources = [];

    if (showSecondOrderReferences && reference) {
      const page = span.getAttribute('data-page');
      const snippetIndex = span.getAttribute('data-index');
      const snippetKey = `${Number(page)}-${Number(snippetIndex)}`;
      const ownExternalId = reference.externalId || reference.id;
      const sources = getSecondOrderReferenceSource({
        reference,
        snippetKey,
        currentResourceDocType: reference?.citation_metadata?.doc_type || '',
      });
      genuineSources = sources.filter((source) => source.externalId !== ownExternalId);
    }

    if (genuineSources.length) {
      return genuineSources.map((source) => {
        // Keyed by text alone (not id::text) — the same source can be cited by more than one
        // parent document (e.g. two ads both citing the same SPC), and should still get a single
        // shared reference-list number, the same way a bibliography lists a cited work once
        // regardless of how many sections reference it. referenceId (coordinate-based, globally
        // unique on its own) is only a fallback for entries with no text.
        const key = normalizeReferenceIdentity(source.text) || source.referenceId || '';
        if (!dict[key]) {
          dict[key] = next++;
          matchFootnotes.push({ customFootnote: source.text, dictKey: key });
        }
        return dict[key];
      });
    }

    if (!(id in dict)) dict[id] = next++;
    return [dict[id]];
  };

  Array.from(tempContainer.querySelectorAll('span[data-doc-id]')).forEach((s) => {
    if (!isCitationBulletVisibleOnDoc(s)) return; // skip hidden citations
    resolveSpanNums(s);
  });

  for (let cur; (cur = tempContainer.querySelector(CITATION_SEL)); ) {
    if (!cur.parentNode) break;

    const { group, between } = nextGroup(cur);

    // unique ids for attribute; numbers resolved per span (possibly several each); compressed text
    const ids = [...new Set(group.map((s) => s.getAttribute('data-doc-id')))];
    const nums = group.flatMap((s) => resolveSpanNums(s));

    const sup = document.createElement('sup');
    sup.textContent = compress(nums).join(',');
    sup.setAttribute('data-doc-ids', ids.join(','));

    const first = group[0];
    first.classList.add('transformed');
    first.parentNode.replaceChild(sup, first);

    group.slice(1).forEach((n) => n.remove()); // remove extra spans
    if (group.length > 1) between.forEach((n) => n.remove()); // remove only BETWEEN punctuation
  }

  // 4) Update references once
  tempContainer.querySelectorAll('[data-ant-field="reference-list"]').forEach((child) => {
    const textElement = child.querySelector('p');
    updateReferencesListForPreviewMode(textElement, dict, matchFootnotes);
  });

  return tempContainer.innerHTML;
};

export const removeCitationBulletsOverlay = ({ iframeDoc, container }) => {
  let existingOverlay = iframeDoc?.getElementById('citation-bullets-overlay');
  if (container) {
    existingOverlay = container.querySelector('#citation-bullets-overlay');
  }
  if (existingOverlay) {
    existingOverlay.remove();
  }
};

// Max citation bullets/badges drawn individually per citation position (in authoring and Assist)
// before collapsing the rest into one "+N" bullet/badge.
export const MAX_INDIVIDUAL_BULLETS = 3;

export const renderCitationBulletsOverIframe = async ({
  iframeDoc,
  mapCitationsDataFromFootnote,
  openDocument,
  saveEditorContentStateFromIframe,
  saveHtmlDocumentContentToDb,
  refreshReferencesOnDelete,
  updateEditorSelectableElementsData,
}) => {
  if (!iframeDoc) return;

  removeCitationBulletsOverlay({ iframeDoc });

  const overlay = iframeDoc.createElement('div');
  overlay.id = 'citation-bullets-overlay';
  Object.assign(overlay.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '9999',
  });
  iframeDoc.body.appendChild(overlay);

  // Inject Google Fonts and styles
  const fontLink = iframeDoc.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = 'https://fonts.googleapis.com/css2?family=DM+Sans&display=swap';
  iframeDoc.head.appendChild(fontLink);

  // Inject styles
  const styleTag = iframeDoc.createElement('style');
  styleTag.innerHTML = tagStyle;
  iframeDoc.head.appendChild(styleTag);

  const spans = Array.from(iframeDoc.querySelectorAll('span[data-doc-id]'));

  // Step 1: Precompute all bullet info
  const dict = {};
  let label = 1;

  // Sequential on purpose (not Promise.all): labels must be assigned in true document order.
  // Each span's citation entries are fully resolved and labeled before moving to the next span,
  // so a second-order bullet's number reflects where it actually falls, not when its promise
  // happened to resolve.
  const bulletInfos = [];

  for (const span of spans) {
    const rect = span.getBoundingClientRect();
    const scrollTop = iframeDoc.documentElement.scrollTop || iframeDoc.body.scrollTop;
    const scrollLeft = iframeDoc.documentElement.scrollLeft || iframeDoc.body.scrollLeft;

    const top = rect.top + scrollTop;
    const left = rect.left + scrollLeft;

    // top is 0 only when element is hidden from layout
    if (top === 0) {
      continue;
    }

    const id = span.getAttribute('data-doc-id');
    const snippetIndex = span.getAttribute('data-index');
    let pageData = span.getAttribute('data-page');
    // Keyed by id+page (not id alone) — the editor's own live-bullet numbering stays scoped to
    // document+page while actively editing, deliberately separate from Preview/export's collapsed
    // (document-wide) numbering; the two only reconcile once you preview or save.
    const dictKey = `${id}-${pageData}`;

    let snippetData = span.getAttribute('data-snippet');
    let updatedByUser = span.getAttribute('data-updated-by-user') === 'true';

    // mapCitationsDataFromFootnote returns one entry per citation to render for this span — for
    // gated orgs, a snippet can have several second-order references, each getting its own
    // bullet+tooltip (as in Assist), instead of just showing the first one.
    const citationEntries = await mapCitationsDataFromFootnote(id, pageData, snippetData, snippetIndex);

    if (!citationEntries?.length) {
      // Do not remove the span from the DOM here — other consumers
      // (footnotes mapping, preview transforms) may still need it.
      // Skip rendering a bullet for this span.
      continue;
    }

    const resolvedEntries = citationEntries.map(({ thumbnailUrl, page, name, snippet, isCustomReference, citationId }) => {
      // Each distinct second-order source gets its own next-available number, reused if that
      // same source (same parent + page + normalized text) repeats later in the document — same
      // as Assist. Bullets without a citationId (no second-order data) fall back to the span's
      // own id-page key. Deliberately NOT reserved upfront: when every entry for this span has a
      // citationId (the main document is entirely replaced by second-order refs), dictKey is
      // never touched, so its number isn't silently burned before the second-order bullets run.
      const bulletDictKey = citationId || dictKey;
      if (!dict[bulletDictKey]) dict[bulletDictKey] = label++;

      return {
        id,
        page,
        label: dict[bulletDictKey],
        top,
        left,
        name,
        snippetData: snippet,
        thumbnailUrl,
        updatedByUser,
        isCustomReference,
        citationId, // in-memory identity for second-order bullets sharing one span; undefined otherwise
        span, // Keep reference to the original span for removal
      };
    });

    // A later entry can reuse an earlier, lower number (same second-order source cited earlier
    // in the document), so citationEntries' raw order doesn't guarantee ascending labels — sort
    // before slicing into visible/overflow so both the drawn bullets and the "+N" tooltip list
    // read in increasing order, same as Assist.
    resolvedEntries.sort((a, b) => (a.label ?? Infinity) - (b.label ?? Infinity));

    span.setAttribute('data-label', resolvedEntries[0].label);

    // Bullets from the same span all start from the same top/left, which makes the generic
    // collision-avoidance search (computePositionBasedOnNeighbors) scatter them via alternating
    // +/- offsets — order 1,2,3 can render visually as 3,1,2. Give each a deterministic
    // left-to-right starting offset here, by its position within this span's own group, so they
    // render in label order; collision-avoidance still applies afterward for bullets from
    // unrelated spans that happen to land nearby.
    const BULLET_GROUP_STEP = 26;
    const withGroupOffset = (entry, indexInSpan) => ({ ...entry, left: entry.left + indexInSpan * BULLET_GROUP_STEP });

    // Cap individually-drawn bullets per citation position at MAX_INDIVIDUAL_BULLETS — a busy
    // snippet with many second-order sources otherwise clutters the document with overlapping
    // bullets. The rest collapse into one "+N" bullet whose tooltip lists them all, each still
    // showing the numbering it would have had as its own bullet.
    if (resolvedEntries.length > MAX_INDIVIDUAL_BULLETS) {
      const visible = resolvedEntries.slice(0, MAX_INDIVIDUAL_BULLETS);
      const overflow = resolvedEntries.slice(MAX_INDIVIDUAL_BULLETS);
      const first = resolvedEntries[0];

      bulletInfos.push(...visible.map((entry, index) => withGroupOffset(entry, index)));
      bulletInfos.push(
        withGroupOffset(
          {
            id,
            page: first.page,
            top,
            left,
            isCustomReference: first.isCustomReference,
            span,
            isOverflow: true,
            label: `+${overflow.length}`,
            overflowEntries: overflow.map((entry) => ({ label: entry.label, name: entry.name })),
          },
          visible.length,
        ),
      );
    } else {
      bulletInfos.push(...resolvedEntries.map((entry, index) => withGroupOffset(entry, index)));
    }
  }

  // Step 2: Batch append to DOM with collision detection
  const bulletElements = [];
  const tooltipElements = [];
  const bulletPositions = []; // Track positions to avoid overlaps
  const elementsBySpan = new Map(); // span -> [{ bullet, tooltip }] — a span can have several bullets

  bulletInfos.forEach((data) => {
    if (!data) {
      return;
    }

    const {
      id,
      page,
      label,
      top,
      left,
      name,
      snippetData,
      thumbnailUrl,
      updatedByUser,
      isCustomReference,
      citationId,
      isOverflow,
      overflowEntries,
      span,
    } = data;

    const bullet = iframeDoc.createElement('div');
    bullet.className = 'info-bullet-wrapper';
    bullet.textContent = label;
    if (citationId) {
      bullet.setAttribute('data-second-order-ref-id', citationId);
    }

    const { bulletTop, bulletLeft } = computePositionBasedOnNeighbors(top, left, bulletPositions);

    // Store the final position
    bulletPositions.push({ top: bulletTop, left: bulletLeft });

    Object.assign(bullet.style, {
      top: `${bulletTop}px`,
      left: `${bulletLeft}px`,
      color: updatedByUser ? '#4CAF50' : '#6222e3',
      border: `2px solid ${updatedByUser ? '#4CAF50' : '#6222e3'}`,
    });

    const removeIconSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path d="M7.5 7.5C7.77614 7.5 8 7.72386 8 8V10C8 10.2761 7.77614 10.5 7.5 10.5C7.22386 10.5 7 10.2761 7 10V8C7 7.72386 7.22386 7.5 7.5 7.5Z" fill="#C64444"/>
            <path d="M10 8C10 7.72386 9.77614 7.5 9.5 7.5C9.22386 7.5 9 7.72386 9 8V10C9 10.2761 9.22386 10.5 9.5 10.5C9.77614 10.5 10 10.2761 10 10V8Z" fill="#C64444"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.71429 4.5V3.1C5.71429 2.66935 5.8922 2.26039 6.20229 1.96194C6.51172 1.66411 6.92747 1.5 7.35714 1.5H9.64286C10.0725 1.5 10.4883 1.66411 10.7977 1.96194C11.1078 2.26039 11.2857 2.66935 11.2857 3.1V4.5H13.5C13.7761 4.5 14 4.72386 14 5C14 5.27614 13.7761 5.5 13.5 5.5H13V11.9C13 12.3306 12.8221 12.7396 12.512 13.0381C12.2026 13.3359 11.7868 13.5 11.3571 13.5H5.64286C5.21318 13.5 4.79743 13.3359 4.488 13.0381C4.17792 12.7396 4 12.3306 4 11.9V5.5H3.5C3.22386 5.5 3 5.27614 3 5C3 4.72386 3.22386 4.5 3.5 4.5H5.71429ZM6.89576 2.68243C7.01498 2.56768 7.18061 2.5 7.35714 2.5H9.64286C9.81939 2.5 9.98502 2.56768 10.1042 2.68243C10.2228 2.79655 10.2857 2.94717 10.2857 3.1V4.5H6.71429V3.1C6.71429 2.94717 6.77718 2.79655 6.89576 2.68243ZM5 5.5V11.9C5 12.0528 5.0629 12.2034 5.18147 12.3176C5.30069 12.4323 5.46633 12.5 5.64286 12.5H11.3571C11.5337 12.5 11.6993 12.4323 11.8185 12.3176C11.9371 12.2034 12 12.0528 12 11.9V5.5H5Z" fill="#C64444"/>
          </svg>
    `;

    const tooltip = iframeDoc.createElement('div');
    tooltip.className = 'tooltip-popup';

    if (isOverflow) {
      const overflowRows = overflowEntries
        .map((entry) => `<div class="tooltip-overflow-item">${entry.label}. ${entry.name}</div>`)
        .join('');

      tooltip.innerHTML = `
        <div class="tooltip-header">
          <div class="tooltip-text">
            <div class="tooltip-title">${overflowEntries.length} more references</div>
          </div>
          <div class="remove-icon">${removeIconSvg}</div>
        </div>
        <div class="tooltip-overflow-list">${overflowRows}</div>
      `;
    } else {
      tooltip.innerHTML = `
      <div class="tooltip-header">
        ${thumbnailUrl ? `<img class="tooltip-image" src="${thumbnailUrl}" alt="thumb"/>` : ''}
        <div class="tooltip-text">
          <div class="tooltip-title">
            <a href="https://google.com/" target="_blank">${name}</a>
          </div>
          <div class="tooltip-subtext">Page ${page}</div>
        </div>
        <div class="remove-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path d="M7.5 7.5C7.77614 7.5 8 7.72386 8 8V10C8 10.2761 7.77614 10.5 7.5 10.5C7.22386 10.5 7 10.2761 7 10V8C7 7.72386 7.22386 7.5 7.5 7.5Z" fill="#C64444"/>
            <path d="M10 8C10 7.72386 9.77614 7.5 9.5 7.5C9.22386 7.5 9 7.72386 9 8V10C9 10.2761 9.22386 10.5 9.5 10.5C9.77614 10.5 10 10.2761 10 10V8Z" fill="#C64444"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.71429 4.5V3.1C5.71429 2.66935 5.8922 2.26039 6.20229 1.96194C6.51172 1.66411 6.92747 1.5 7.35714 1.5H9.64286C10.0725 1.5 10.4883 1.66411 10.7977 1.96194C11.1078 2.26039 11.2857 2.66935 11.2857 3.1V4.5H13.5C13.7761 4.5 14 4.72386 14 5C14 5.27614 13.7761 5.5 13.5 5.5H13V11.9C13 12.3306 12.8221 12.7396 12.512 13.0381C12.2026 13.3359 11.7868 13.5 11.3571 13.5H5.64286C5.21318 13.5 4.79743 13.3359 4.488 13.0381C4.17792 12.7396 4 12.3306 4 11.9V5.5H3.5C3.22386 5.5 3 5.27614 3 5C3 4.72386 3.22386 4.5 3.5 4.5H5.71429ZM6.89576 2.68243C7.01498 2.56768 7.18061 2.5 7.35714 2.5H9.64286C9.81939 2.5 9.98502 2.56768 10.1042 2.68243C10.2228 2.79655 10.2857 2.94717 10.2857 3.1V4.5H6.71429V3.1C6.71429 2.94717 6.77718 2.79655 6.89576 2.68243ZM5 5.5V11.9C5 12.0528 5.0629 12.2034 5.18147 12.3176C5.30069 12.4323 5.46633 12.5 5.64286 12.5H11.3571C11.5337 12.5 11.6993 12.4323 11.8185 12.3176C11.9371 12.2034 12 12.0528 12 11.9V5.5H5Z" fill="#C64444"/>
          </svg>
        </div>
      </div>
      <div class="tooltip-snippet">${snippetData || ''}</div>
      `;
    }

    let tooltipTop = top - 160;
    let tooltipLeft = left + 25;
    const tooltipWidth = 300;
    const viewportWidth = iframeDoc.documentElement.clientWidth;
    if (tooltipTop < 0) tooltipTop = top + 10;
    if (tooltipLeft + tooltipWidth > viewportWidth) tooltipLeft = left - tooltipWidth;

    Object.assign(tooltip.style, {
      top: `${tooltipTop + 80}px`,
      left: `${tooltipLeft}px`,
    });

    let hideTimeout;
    const showTooltip = () => {
      clearTimeout(hideTimeout);
      tooltip.classList.add('visible');
    };
    const hideTooltip = () => {
      hideTimeout = setTimeout(() => tooltip.classList.remove('visible'), 200);
    };

    //click events
    if (!isCustomReference) {
      bullet.addEventListener('click', () => {
        openDocument({ reference: { externalId: id }, page });
      });
      tooltip.addEventListener('click', () => {
        openDocument({ reference: { externalId: id }, page });
      });
    }

    //hover events
    bullet.addEventListener('mouseenter', showTooltip);
    bullet.addEventListener('mouseleave', hideTooltip);
    tooltip.addEventListener('mouseenter', showTooltip);
    tooltip.addEventListener('mouseleave', hideTooltip);

    // Track this bullet/tooltip pair against its source span so removal (below) can clean up
    // every bullet spawned from the same span (e.g. multiple second-order-reference bullets).
    if (!elementsBySpan.has(span)) {
      elementsBySpan.set(span, []);
    }
    elementsBySpan.get(span).push({ bullet, tooltip });

    // Add functionality to remove the span
    const removeIcon = tooltip.querySelector('.remove-icon');
    removeIcon.addEventListener('click', (event) => {
      event.stopPropagation(); // Prevent triggering other click events
      const spanToRemove = span;
      if (spanToRemove) {
        spanToRemove.remove(); // Remove the span from the DOM
        const siblings = elementsBySpan.get(spanToRemove) || [{ bullet, tooltip }];
        siblings.forEach(({ bullet: siblingBullet, tooltip: siblingTooltip }) => {
          siblingBullet.remove();
          siblingTooltip.remove();
        });
        updateEditorSelectableElementsData();
        // Persist the change: save iframe state then save to DB so the removal survives refresh
        setTimeout(async () => {
          try {
            refreshReferencesOnDelete();
            saveEditorContentStateFromIframe();
            if (typeof saveHtmlDocumentContentToDb === 'function') {
              await saveHtmlDocumentContentToDb();
            }
          } catch (e) {
            console.error('Failed to persist removed citation span', e);
          }
        }, 500);
      }
    });

    bulletElements.push(bullet);
    tooltipElements.push(tooltip);
  });

  // Append all at once to prevent layout thrashing
  bulletElements.forEach((el) => overlay.appendChild(el));
  tooltipElements.forEach((el) => overlay.appendChild(el));
};

const tagStyle = `
    * {
      font-family: 'DM Sans', sans-serif !important;
    }
    .info-bullet-wrapper {
      position: absolute;
      width: 24px;
      height: 24px;
      background-color: #fff;
      border: 2px solid #6222e3;
      color: #6222e3;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
      cursor: pointer;
      z-index: 10000;
      pointer-events: auto;
    }
    .tooltip-popup {
      position: absolute;
      width: 300px;
      background-color: white;
      border: 1px solid #ddd;
      border-radius: 8px;
      box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
      padding: 0;
      display: none;
      flex-direction: column;
      z-index: 10001;
      pointer-events: auto;
    }
    .tooltip-popup.visible {
      display: flex;
    }
    .tooltip-header {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      background-color: #e8e7e5;
      padding: 12px 12px 4px 12px;
      margin-bottom: -2px;
      border-radius: 7px 7px 0 0;
    }
    .tooltip-image {
      width: 60px;
      height: 48px;
      background-color: #ccc;
      margin-right: 8px;
    }
    .tooltip-text {
      flex-grow: 1;
    }
    .tooltip-title {
      font-weight: bold;
      color: #6222e3;
      font-size: 13px;
      margin-bottom: 4px;
      cursor: pointer;
      max-height: 130px;
      max-width: 200px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      a {
        text-decoration: none !important;
      }
    }
    .tooltip-subtext {
      line-height: 16px;
      font-weight: 500;
      font-style: normal;
      color: #8e8d8b;
      letter-spacing: .1px;
      font-size: 13px;
    }
    .tooltip-snippet {
      font-size: 12px;
      line-height: 1.5;
      padding: 12px;
      color: #333;
      max-height: 46px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }
    .remove-icon {
      position: absolute;
      right: 4px;
      top: 4px;
      font-size: 12px;
      cursor: pointer;
      z-index: 10001;
    }
    .tooltip-overflow-list {
      padding: 8px 12px 12px 12px;
      max-height: 160px;
      overflow-y: auto;
    }
    .tooltip-overflow-item {
      font-size: 12px;
      line-height: 1.5;
      color: #333;
      padding: 4px 0;
    }
    .tooltip-overflow-item + .tooltip-overflow-item {
      border-top: 1px solid #e8e7e5;
    }
  `;

// regex for matching citation placeholders in raw message text.
export const CITATION_PLACEHOLDER_RGX = /\[([^\]\n,]+),\s*p\.(\d+)\]/g;

/**
 * Splits a raw citation placeholder id into the document externalId and its snippet index.
 * The snippet index is an optional trailing `-N` suffix (e.g. `docId-2` → id `docId`, snippetIndex `2`);
 * a trailing segment is only treated as the index when it's entirely digits, so document ids that
 * themselves end in a hyphenated number without a snippet suffix fall back to snippetIndex '0'.
 * @param {string} rawId - The id portion captured by CITATION_PLACEHOLDER_RGX, before the `, p.N`.
 * @returns {{id: string, snippetIndex: string}} The split id and snippet index (see CitationEntry).
 */
export const parseCitationPlaceholderId = (rawId = '') => {
  const separatorIndex = rawId.lastIndexOf('-');
  if (separatorIndex === -1) {
    return { id: rawId, snippetIndex: '0' };
  }

  const possibleSnippetIndex = rawId.slice(separatorIndex + 1);
  const isSnippetIndex =
    possibleSnippetIndex.length > 0 && [...possibleSnippetIndex].every((char) => char >= '0' && char <= '9');

  if (!isSnippetIndex) {
    return { id: rawId, snippetIndex: '0' };
  }

  return {
    id: rawId.slice(0, separatorIndex),
    snippetIndex: possibleSnippetIndex,
  };
};

/**
 * Extracts every `[docId(-N), p.M]` citation placeholder from raw message text, in text order.
 * @param {string} content - Raw markdown/message text to scan.
 * @returns {CitationEntry[]} Placeholders found, in text order.
 */
export const extractCitationEntries = (content = '') => {
  const entries = [];
  const rgx = new RegExp(CITATION_PLACEHOLDER_RGX.source, 'g');
  let match;

  while ((match = rgx.exec(content)) !== null) {
    const [, rawId, page] = match;
    const { id, snippetIndex } = parseCitationPlaceholderId(rawId);
    entries.push({ id, snippetIndex, page });
  }

  return entries;
};

/**
 * Shared core for buildRefNumCitationDict and buildCitationDictFromPlaceholders. Walks citation
 * entries in document order and assigns each a running label number (1, 2, 3, ...), reusing the
 * same number if that exact key is seen again later. Two kinds of keys:
 * - `${id}-${text||referenceId}` for citations backed by real second-order data — grouped by the
 *   citation's own text rather than its (physical, coordinate-based) referenceId, so the same
 *   source repeated at multiple locations (e.g. a footer reference list on every page) shares one
 *   label instead of getting a new one per occurrence.
 * - `${id}-${snippetIndex}` for citations with no second-order data for that snippet (repeat
 *   citations of the same doc+snippet stay numbered consistently).
 * @param {CitationEntry[]} entries - Citation occurrences, in order.
 * @param {object[]} references - Document reference objects (carrying second_order_references_by_snippet).
 * @returns {Record<string, number>} Map of composite key → assigned label.
 */
const _buildRefNumDictFromEntries = (entries = [], references = []) => {
  const dict = {};
  let label = 1;

  for (const { id, snippetIndex, page } of entries) {
    const snippetKey = `${page}-${snippetIndex}`;
    const secondOrderRefs = references
      .filter((ref) => ref.externalId === id)
      .flatMap((ref) => ref.second_order_references_by_snippet?.[snippetKey] || []);

    if (secondOrderRefs.length > 0) {
      // Group by the citation's own text (not referenceId) — the same source can appear at
      // multiple physical locations (e.g. a reference list repeated in a footer on every page),
      // each with a distinct coordinate-based referenceId, but it's still the same source.
      secondOrderRefs.forEach(({ referenceId, text }) => {
        const dictKey = `${id}-${text || referenceId}`;
        if (!(dictKey in dict)) {
          dict[dictKey] = label++;
        }
      });
    } else if (!(`${id}-${snippetIndex}` in dict)) {
      dict[`${id}-${snippetIndex}`] = label++;
    }
  }

  return dict;
};

/**
 * Builds the citation label dict for a rendered message (see _buildRefNumDictFromEntries).
 * @param {CitationEntry[]} renderedCitationEntries - Placeholders found in the message, in order.
 * @param {object[]} references - Document reference objects for this message.
 * @returns {Record<string, number>} Map of composite key → assigned label.
 */
export const buildRefNumCitationDict = (renderedCitationEntries = [], references = []) =>
  _buildRefNumDictFromEntries(renderedCitationEntries, references);

const getRenderedChunkContents = (message = {}) => {
  if (!message.chunks?.length) {
    return [message.message || ''];
  }

  return message.chunks.flatMap((chunk) => {
    if (chunk.type === CHUNK_TYPE.TEXT) {
      return [chunk.content].filter(Boolean);
    }
    if (chunk.type === CHUNK_TYPE.RESEARCH) {
      return [chunk.data?.value].filter(Boolean);
    }
    return [];
  });
};

/**
 * All citation placeholder entries across a whole message (both legacy `message.message` and
 * chunked/new-agent messages), in document order.
 * @param {object} message - A conversation message; either `message.message` (legacy) or
 *   `message.chunks` (new-agent) is read, whichever the message shape provides.
 * @returns {CitationEntry[]} Placeholders found, in text order.
 */
export const getRenderedCitationEntries = (message = {}) =>
  getRenderedChunkContents(message).flatMap((content) => extractCitationEntries(content || ''));

/**
 * Extracts the unique set of documentIds cited in a message's raw text, in first-seen order.
 * Used to build the legacy `citedReferences` list (one entry per distinct cited document, not
 * per second-order source).
 * @param {string} message - Raw message text.
 * @returns {{documentId: string}[]} One entry per distinct cited document, in first-seen order.
 */
export const extractReferencesFromCitations = (message = '') => {
  const citationRegex = new RegExp(CITATION_PLACEHOLDER_RGX.source, 'g');
  const citations = [];
  let match;

  while ((match = citationRegex.exec(message)) !== null) {
    const [, rawId, page] = match;
    const { id } = parseCitationPlaceholderId(rawId);
    citations.push({ documentId: id, page: parseInt(page, 10) });
  }

  return citations.reduce((acc, citation) => {
    if (!acc.find((existing) => existing.documentId === citation.documentId)) {
      acc.push({ documentId: citation.documentId });
    }
    return acc;
  }, []);
};

/**
 * Enriches a message's legacy documentList block with `citedReferences` (one per distinct cited
 * document) and `secondOrderReferences` (one per unique second-order source, labeled/deduped —
 * see buildSecondOrderReferences). Runs once when a conversation is loaded (see
 * useConversations.js/conversationStore.js) — the result is cached on message.data[0], not
 * recomputed reactively, so a conversation must be reloaded to pick up logic changes here.
 * @param {string} messageText - Raw message text (for extracting citation placeholders).
 * @param {object[]} blockData - message.data; must contain a `documentList` block.
 * @returns {object[]} `blockData` unchanged (same reference) if it's empty or has no `documentList`
 *   block; otherwise a new array with that block's citedReferences/secondOrderReferences replaced.
 */
export const processCitedReferences = (messageText, blockData) => {
  if (!blockData || blockData.length === 0) return blockData;

  const documentListBlock = blockData.find((block) => block.type === 'documentList');
  if (!documentListBlock) return blockData;

  const citedRefs = extractReferencesFromCitations(messageText || '');
  const references = Array.isArray(documentListBlock.references) ? documentListBlock.references : [];
  const citedReferences = citedRefs
    .map((citedRef) => references.find((ref) => ref.externalId === citedRef.documentId))
    .filter(Boolean);

  const citationEntries = extractCitationEntries(messageText || '');
  const secondOrderReferencesBySnippet = buildSecondOrderReferencesBySnippet({
    references,
    renderedCitationEntries: citationEntries,
  });
  const citationLabelDict = buildRefNumCitationDict(citationEntries, references);
  const secondOrderReferences = buildSecondOrderReferences(
    { message: messageText || '' },
    secondOrderReferencesBySnippet,
    citationLabelDict,
  );

  return [{ ...documentListBlock, citedReferences, secondOrderReferences }];
};

const getCurrentResourceDocType = (reference = {}) => reference.citation_metadata?.doc_type || '';

const parseCitationNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

/**
 * Finds the rendered citation entry (an actual `[docId, p.N]` occurrence in the message text)
 * matching a given reference's snippetKey — i.e. confirms this snippet was actually cited, not
 * just present in the reference's data. Falls back to a doc-id+page match when the exact
 * page-snippetIndex key is unavailable (backend snippet indexes can diverge from text indexes).
 * @param {CitationEntry[]} renderedCitationEntries - Placeholders found in the message text.
 * @param {object} reference - The document reference object.
 * @param {string} snippetKey - `${page}-${snippetIndex}` key into reference.snippets.
 * @returns {CitationEntry|undefined} The matching placeholder, or undefined if this snippet was never actually cited.
 */
const getRenderedCitationEntry = (renderedCitationEntries = [], reference = {}, snippetKey = '') => {
  const externalId = reference.externalId || '';
  const [snippetPage = ''] = String(snippetKey).split('-');

  const exactMatch = renderedCitationEntries.find(({ id, snippetIndex, page }) => {
    if (`${page}-${snippetIndex}` !== snippetKey) {
      return false;
    }

    return id === externalId || `${id}-${snippetIndex}` === externalId;
  });

  if (exactMatch) {
    return exactMatch;
  }

  // Fallback: backend snippet indexes can diverge from citation text indexes.
  // Match by document id + page when exact page-index key is unavailable.
  return renderedCitationEntries.find(({ id, page }) => {
    if (String(page) !== String(snippetPage)) {
      return false;
    }

    return id === externalId || externalId.startsWith(`${id}-`);
  });
};

/**
 * Finds the document reference object for a citation by externalId, allowing for the
 * `externalId-snippetIndex` id shape as well as the bare externalId.
 * @param {{references: object[], id: string, snippetIndex: string}} params - references to search,
 *   plus the citation's document id and snippet index.
 * @returns {object|undefined} The matching reference object, or undefined if not found.
 */
const findCitationReference = ({ references = [], id = '', snippetIndex = '0' } = {}) =>
  references.find((ref) => ref?.externalId === id || ref?.externalId === `${id}-${snippetIndex}`);

/**
 * The raw per-snippet second-order reference map for a document (snake_case or camelCase field,
 * whichever the backend sent). Keyed by `${page}-${snippetIndex}`; each value is either an array
 * of {referenceId, refNum, text} entries, an empty array (checked, nothing found — unsubstantiated),
 * or the key is simply absent (never checked for that snippet).
 * @param {object} reference - The document reference object.
 * @returns {Record<string, object[]>} Map of `${page}-${snippetIndex}` → raw second-order entries.
 */
const getSecondOrderReferencesBySnippet = (reference = {}) =>
  reference?.second_order_references_by_snippet || reference?.secondOrderReferencesBySnippet || {};

/**
 * Whether a document is flagged as a primary source (`citation_metadata.is_primary_source`) — a
 * single per-document flag, not per-snippet. When true, a citation with no second-order data for
 * its snippet is treated as "cites the document itself" rather than "unsubstantiated."
 * @param {object} reference - The document reference object.
 * @returns {boolean}
 */
const isPrimarySourceDocument = (reference = {}) => Boolean(reference?.citation_metadata?.is_primary_source);

/**
 * Whether a specific citation (id+page+snippetIndex) is unsubstantiated: the backend explicitly
 * checked that citing snippet and found no second-order reference (an empty array, not a missing
 * key — see getSecondOrderReferencesBySnippet), and the document isn't flagged as a primary source.
 * @param {{references: object[], id: string, snippetIndex: string, page: string}} params - references
 *   to search, plus the citation's document id, snippet index, and page.
 * @returns {boolean}
 */
export const isUnsubstantiatedCitation = ({ references = [], id = '', snippetIndex = '0', page = '' } = {}) => {
  const reference = findCitationReference({ references, id, snippetIndex });
  const bySnippet = getSecondOrderReferencesBySnippet(reference);
  const snippetKey = `${page}-${snippetIndex}`;

  const hasEmptySecondOrderData = Array.isArray(bySnippet[snippetKey]) && bySnippet[snippetKey].length === 0;

  return hasEmptySecondOrderData && !isPrimarySourceDocument(reference);
};

/**
 * Resolves what "source" to show for a given citing snippet: real second-order reference entries
 * if the backend recorded any for this exact snippet, otherwise a single synthetic entry
 * representing `reference` itself — flagged `isPrimarySource: true` when the document is a
 * primary source, or `unsubstantiated: true` otherwise. Shared by chat (buildSecondOrderReferencesBySnippet)
 * and authoring (transformDocReferences/renderCitationBulletsOverIframe) so both resolve identically.
 * @param {{reference: object, snippetKey: string, currentResourceDocType: string}} params - the
 *   citing document reference, its `${page}-${snippetIndex}` key, and its resolved doc_type.
 * @returns {object[]} Always non-empty: either the real second-order entries recorded for this
 *   snippet, or exactly one synthetic entry ({referenceId: null, text, isPrimarySource, unsubstantiated}).
 */
export const getSecondOrderReferenceSource = ({ reference = {}, snippetKey = '', currentResourceDocType = '' } = {}) => {
  const bySnippet = getSecondOrderReferencesBySnippet(reference);
  const snippetSpecificSource = Array.isArray(bySnippet[snippetKey]) ? bySnippet[snippetKey] : null;

  if (snippetSpecificSource?.length) {
    return snippetSpecificSource;
  }

  const isPrimarySource = isPrimarySourceDocument(reference);

  return [
    {
      externalId: reference.externalId,
      refNum: null,
      text: reference.name,
      isPrimarySource,
      unsubstantiated: !isPrimarySource,
      doc_type: currentResourceDocType,
    },
  ];
};

/**
 * Normalizes a raw second-order entry (from getSecondOrderReferenceSource) into the canonical
 * shape used throughout the citations pipeline and the UI (title/label/passage rendering).
 * @param {object} entry - Raw entry: {referenceId, refNum, text, unsubstantiated?, isPrimarySource?, ...}.
 * @param {string} snippet - Base64-encoded citing passage text (reference.snippets[snippetKey]).
 * @param {number|null} page - Page number of the citing snippet.
 * @param {number|null} snippetIndex - Snippet index of the citing snippet.
 * @param {string} citationKey - `${id}-${page}-${snippetIndex}` identity of the citing occurrence.
 * @returns {NormalizedSecondOrderReference}
 */
const normalizeSecondOrderReference = (
  entry = {},
  snippet = '',
  page = null,
  snippetIndex = null,
  citationKey = '',
) => ({
  referenceId: entry.referenceId || entry.reference_id || '',
  externalId: entry.externalId || entry.parentExternalId || entry.currentExternalId || '',
  parentExternalId: entry.parentExternalId || entry.currentExternalId || '',
  citationId: entry.citationId || citationKey,
  refNum: entry.refNum || entry.ref_num || null,
  text: entry.text || '',
  name: entry.name || 'Second-order reference',
  page,
  citationKey,
  snippetIndex,
  unsubstantiated: Boolean(entry.unsubstantiated),
 isPrimarySource: Boolean(entry.isPrimarySource),
  doc_type: entry.doc_type || '',
  snippet,
  decodedSnippet: decodeFromBase64(snippet || ''),
});

/**
 * Builds, per citing snippet, the list of normalized second-order reference items for a whole
 * message. Only snippets that were actually cited in the text (per renderedCitationEntries) are
 * included. Deduped per-snippet by citation text (falling back to referenceId) — see the
 * dedupeKeyFor comment below for why refNum isn't part of that identity.
 * @param {{references: object[], renderedCitationEntries: CitationEntry[]}} params - Document
 *   references for the message, plus every citation placeholder actually rendered in its text.
 * @returns {Record<string, NormalizedSecondOrderReference[]>} Map of `${page}-${snippetIndex}` →
 *   normalized items. A key can map to an empty array (the snippetKey was cited but
 *   getSecondOrderReferenceSource's guaranteed-non-empty result was still filtered out below) —
 *   in practice this shouldn't currently happen, since getSecondOrderReferenceSource never
 *   returns an empty array, but the `if (!source.length)` guard below assumes it can.
 */
export const buildSecondOrderReferencesBySnippet = ({ references = [], renderedCitationEntries = [] } = {}) =>
  references.reduce((acc, reference = {}) => {
    const snippets = reference.snippets || {};
    const bySnippet = getSecondOrderReferencesBySnippet(reference);
    const snippetKeys = [...new Set([...Object.keys(snippets), ...Object.keys(bySnippet)])];
    const currentResourceDocType = getCurrentResourceDocType(reference);

    snippetKeys.forEach((snippetKey) => {
      const renderedCitationEntry = getRenderedCitationEntry(renderedCitationEntries, reference, snippetKey);
      if (!renderedCitationEntry) {
        return;
      }

      if (!acc[snippetKey]) {
        acc[snippetKey] = [];
      }

      const [snippetPage = '1', snippetIndex = '0'] = String(snippetKey).split('-');
      const parsedPage = parseCitationNumber(snippetPage, 1);
      const citationKey = `${renderedCitationEntry.id}-${renderedCitationEntry.page}-${renderedCitationEntry.snippetIndex}`;
      const source = getSecondOrderReferenceSource({
        reference,
        snippetKey,
        currentResourceDocType,
      });

      if (!source.length) {
        return;
      }

      const normalized = source
        .map((entry) =>
          normalizeSecondOrderReference(
            {
              ...entry,
              parentExternalId: reference.externalId,
              currentExternalId: reference.externalId,
              name: reference.name,
              doc_type: currentResourceDocType,
              citationId: citationKey,
            },
            snippets[snippetKey] || '',
            parsedPage,
            parseCitationNumber(snippetIndex, 0),
            citationKey,
          ),
        )
        .filter((item) => item.referenceId || item.text);

      if (!normalized.length) {
        return;
      }

      // Keyed by text (not refNum): refNum is that page's own local reference-list numbering and
      // isn't guaranteed stable for the same source, so requiring it to match too could let a
      // genuine duplicate slip through as a second "Passage N" row downstream. text is preferred,
      // falling back to referenceId only when text is missing — same identity rule used
      // everywhere else this source gets deduped/grouped/numbered.
      const dedupeKeyFor = (item) => `${item.externalId}-${item.text || item.referenceId || ''}`;
      const existingKeys = new Set(acc[snippetKey].map(dedupeKeyFor));

      normalized.forEach((item) => {
        const dedupeKey = dedupeKeyFor(item);
        if (!existingKeys.has(dedupeKey)) {
          acc[snippetKey].push(item);
          existingKeys.add(dedupeKey);
        }
      });
    });

    return acc;
  }, {});

/**
 * Builds the final, ordered list of second-order reference "cards" for a message: one per unique
 * citation label (from citationLabelDict, the same numbering rendered as bullets in the text), each
 * carrying every passage where that source was cited (`.passages`) so a source cited more than once,
 * or at multiple physical locations, still shows every occurrence under a single label.
 * @param {object} message - Unused directly but kept for parity with authoring's equivalent resolvers.
 * @param {Record<string, NormalizedSecondOrderReference[]>} secondOrderReferencesBySnippet - From buildSecondOrderReferencesBySnippet.
 * @param {Record<string, number>} citationLabelDict - From buildRefNumCitationDict, same key scheme.
 * @returns {SecondOrderReferenceLabel[]} One entry per label, sorted ascending.
 */
export const buildSecondOrderReferences = (message, secondOrderReferencesBySnippet = {}, citationLabelDict = {}) => {
  const allItems = Object.values(secondOrderReferencesBySnippet)
    .flat()
    .filter((item) => item && item.externalId);

  // One entry per unique citation label — dictated by citationLabelDict (same source of truth as
  // what is rendered in the text). This guarantees the References list count matches exactly.
  return Object.entries(citationLabelDict)
    .sort(([, a], [, b]) => a - b)
    .map(([dictKey, label]) => {
      // Match by ${externalId}-${text||referenceId} (second-order) or ${externalId}-${snippetIndex}
      // (fallback). Grouping by text (not referenceId) means the same source appearing at multiple
      // physical locations (e.g. a repeated footer reference list) collapses into one label instead
      // of being treated as distinct references. filter (not find) so that the same source appearing
      // in multiple snippets is collected as multiple passages under a single label.
      const matchingItems = allItems.filter((item) =>
        item.referenceId
          ? dictKey === `${item.externalId}-${item.text || item.referenceId}`
          : dictKey === `${item.externalId}-${item.snippetIndex}`,
      );

      if (!matchingItems.length) return null;

      const primary = matchingItems[0];
      return {
        ...primary,
        index: label,
        citationId: primary.citationId || primary.citationKey || dictKey,
        citationKey: primary.citationKey || primary.citationId || dictKey,
        referenceId: primary.referenceId,
        externalId: primary.parentExternalId || primary.externalId,
        passages: matchingItems,
      };
    })
    .filter(Boolean);
};

/**
 * Source badge data (name/logo/type) for a document id — currently only distinguishes Veeva
 * (internal) documents from everything else (external, and defaulted to unsubstantiated).
 * @param {string} id - Document externalId; falsy (or non-Veeva) resolves to the 'external'/'Unknown' default.
 * @returns {{name: string, logo: string|null, type: 'internal'|'external', unsubstantiated: boolean}}
 */
export const getCitationSourceData = (id) => {
  if (id) {
    const isVeevaId = isVeevaDocument(id);

    if (isVeevaId) {
      return {
        name: 'Veeva',
        logo: veevaLogo,
        type: 'internal',
        unsubstantiated: false,
      };
    }
  }

  return {
    name: 'Unknown',
    logo: null,
    type: 'external',
    unsubstantiated: true,
  };
};

export const isCitationBulletVisibleOnDoc = (el) => {
  let node = el;

  while (node) {
    if (node.hasAttribute && node.hasAttribute('hidden')) {
      return false;
    }

    if (node.getAttribute) {
      const style = node.getAttribute('style') || '';
      if (/display\s*:\s*none/.test(style) || /visibility\s*:\s*hidden/.test(style)) {
        return false;
      }
    }

    node = node.parentElement;
  }

  return true;
};

/**
 * Builds the citation label dict (see _buildRefNumDictFromEntries) from DOM `.citation-placeholder`
 * elements instead of parsed text entries — used by TextMessageWithTooltip.vue after
 * sanitizeAndParse has turned raw `[id-N, p.M]` markers into placeholder spans.
 * @param {NodeListOf<Element>|Element[]} placeholders - Elements with data-id/data-index/data-page.
 * @param {object[]} references - Document reference objects for this message.
 * @returns {Record<string, number>} Map of composite key → assigned label.
 */
export const buildCitationDictFromPlaceholders = (placeholders, references = []) => {
  const entries = Array.from(placeholders).map((ph) => ({
    id: ph.getAttribute('data-id'),
    snippetIndex: ph.getAttribute('data-index'),
    page: ph.getAttribute('data-page'),
  }));
  return _buildRefNumDictFromEntries(entries, references);
};
